// Ordersproduct.js - UI matched with OrderstatusImage (instant load: cached list + parallel requests)
// Order se jura hua: edit usi line ko badalta hai aur order ka total bhi badal jata hai.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaSearch,
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheckDouble,
  FaEllipsisV,
  FaBox,
  FaChevronDown,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { RiDeleteBinFill } from 'react-icons/ri';
import { IoMdArrowDropdown } from 'react-icons/io';
import { MdOutlineProductionQuantityLimits } from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useCurrency } from '../../context/CurrencyContext';
import './Ordersproduct.css';

// Last loaded ordered-products list of each shop is kept, so the list shows
// instantly on the next visit while fresh data loads in the background.
// (v2: ab har item me order ki tracking id bhi hai)
const ORDERSPRODUCT_CACHE_PREFIX = 'ordersproduct_cache_v2_';

const readOrdersCache = (id) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(`${ORDERSPRODUCT_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeOrdersCache = (id, data) => {
  if (!id) return;
  try {
    localStorage.setItem(`${ORDERSPRODUCT_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

// ✅ Server ke error se asli message nikalta hai (Django ka HTML page kabhi popup me nahi dikhega)
const extractServerErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('<')) {
      const match = trimmed.match(/<pre class="exception_value">([\s\S]*?)<\/pre>/);
      if (match) {
        const text = match[1].replace(/<[^>]*>/g, '').replace(/&quot;/g, '"').replace(/&#x27;/g, "'").replace(/&amp;/g, '&').trim();
        if (text) return `Server error: ${text}`;
      }
      return fallback;
    }
    return trimmed || fallback;
  }

  if (data.error) return data.error;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  return fallback;
};

// Error / success popups hamesha modal ke UPAR dikhen
const SWAL_ON_TOP_CSS = `.swal2-container { z-index: 100000 !important; }`;

// ========== CUSTOM DROPDOWN COMPONENT ==========
const CustomDropdown = ({ options, value, onChange, placeholder, disabled, loading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="custom-dropdown" ref={dropdownRef}>
      <div
        className={`custom-dropdown-selected ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span className="selected-text">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FaChevronDown className="arrow" />
      </div>

      {isOpen && !disabled && !loading && (
        <div className="custom-dropdown-list">
          {options.length === 0 ? (
            <div className="custom-dropdown-item" style={{ color: '#999', cursor: 'default' }}>
              No options available
            </div>
          ) : (
            options.map((opt) => (
              <div
                key={opt.value}
                className={`custom-dropdown-item ${String(opt.value) === String(value) ? 'selected' : ''}`}
                onClick={() => handleSelect(opt.value)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
// ========== END CUSTOM DROPDOWN ==========

const emptyEditProduct = {
  id: null,
  shop_id: '',
  product_Id: '',
  order_Id: '',
  order_tracking: '',
  quantity: 1,
  custom_price: 0,
  price_touched: false,
  city: ''
};

const Ordersproduct = () => {
  const { shopId } = useParams(); // shop id pehle render se hi URL se mil jati hai
  const navigate = useNavigate();
  const { formatPrice, currency, loading: currencyLoading } = useCurrency();

  const [searchTerm, setSearchTerm] = useState('');
  // Saved list of this shop is shown right away (if there is one)
  const [orders, setOrders] = useState(() => readOrdersCache(shopId)?.orders || []);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  // True only while there is nothing to show yet (so "No ordered products" never flashes)
  const [loading, setLoading] = useState(() => !readOrdersCache(shopId));
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadingShops, setLoadingShops] = useState(false);
  const [currentShopName, setCurrentShopName] = useState(() => readOrdersCache(shopId)?.shopName || 'Loading...');

  const [shops, setShops] = useState([]);
  const [shopCities, setShopCities] = useState([]);

  // City filter for header
  const [selectedFilterCity, setSelectedFilterCity] = useState('');
  const [loadingFilterCities, setLoadingFilterCities] = useState(false);
  const [cityCounts, setCityCounts] = useState(() => readOrdersCache(shopId)?.cityCounts || {});

  const [showShopModal, setShowShopModal] = useState(false);
  const [newShopData, setNewShopData] = useState({
    shop_name: '',
    subdomain: '',
    domain: 'hira-tech.co.uk',
    shop_email: '',
    shop_phone: '',
    shop_address: ''
  });

  const [productOptions, setProductOptions] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const ordersRequestRef = useRef(0);   // sirf sab se naya request screen update kar sakta hai
  const hasLoadedShopsRef = useRef(false);
  const lastFetchRef = useRef(0);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/order';
  const PRODUCT_API_URL = 'https://super-backend.hira-tech.co.uk/api/product';
  const SHOPS_API_URL = 'https://super-backend.hira-tech.co.uk/order/shops/';

  const [newOrderProduct, setNewOrderProduct] = useState({
    shop_id: '',
    product_Id: '',
    order_Id: '',
    quantity: 1,
    custom_price: 0,
    city: ''
  });

  const [editProduct, setEditProduct] = useState(emptyEditProduct);

  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#004aad'
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: message,
      confirmButtonColor: '#d33'
    });
  };

  const showWarningAlert = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Warning!',
      text: message,
      confirmButtonColor: '#f0ad4e'
    });
  };

  // Orders ki list ka saved cache hata do taake wahan purana total na dikhe
  const invalidateOrdersListCache = () => {
    try { localStorage.removeItem(`orders_cache_${shopId}`); } catch (e) {}
  };

  // ============== FETCH SHOPS ==============
  const fetchShops = async () => {
    setLoadingShops(true);
    try {
      const response = await axios.get(SHOPS_API_URL);
      let shopsData = [];
      if (Array.isArray(response.data)) {
        shopsData = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        shopsData = response.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        shopsData = response.data.data;
      } else if (response.data.shops && Array.isArray(response.data.shops)) {
        shopsData = response.data.shops;
      }
      setShops(shopsData);

      if (!shopId && shopsData.length > 0) {
        const firstShop = shopsData[0];
        navigate(`/shop/${firstShop.shop_id}/Orders-product`, { replace: true });
      }
      return shopsData;
    } catch (error) {
      console.error('Error fetching shops:', error);
      setShops([]);
      return [];
    } finally {
      setLoadingShops(false);
    }
  };

  // ============== FETCH SHOP CITIES ==============
  const fetchShopCities = useCallback(async (shop_id) => {
    if (!shop_id) {
      setShopCities([]);
      setSelectedFilterCity('');
      return;
    }
    try {
      setLoadingFilterCities(true);
      const url = `${API_BASE_URL}/shop-cities/?shop_id=${shop_id}`;
      const response = await axios.get(url);

      let citiesData = [];
      if (response.data && response.data.cities) {
        citiesData = response.data.cities;
      } else if (Array.isArray(response.data)) {
        citiesData = response.data;
      }

      setShopCities(citiesData);
      setSelectedFilterCity('');
    } catch (err) {
      console.error('Error fetching shop cities:', err);
      setShopCities([]);
      setSelectedFilterCity('');
    } finally {
      setLoadingFilterCities(false);
    }
  }, [API_BASE_URL]);

  // ============== CREATE SHOP ==============
  const createShop = async (shopData) => {
    try {
      await axios.post(SHOPS_API_URL, shopData);
      showSuccessAlert('Shop created successfully!');
      setShowShopModal(false);
      await fetchShops();
      setNewShopData({
        shop_name: '',
        subdomain: '',
        domain: 'hira-tech.co.uk',
        shop_email: '',
        shop_phone: '',
        shop_address: ''
      });
    } catch (error) {
      console.error('Error creating shop:', error);
      showErrorAlert('Failed to create shop');
    }
  };

  // ============== FETCH ORDERED PRODUCTS ==============
  // Har order ke products ab ek ek karke nahi, saath saath (parallel) aate hain,
  // aur list pehle batch ke baad hi dikhna shuru ho jati hai.
  const fetchOrderedProducts = useCallback(async () => {
    if (!shopId) return;

    // Only the latest request is allowed to update the screen
    const requestId = ++ordersRequestRef.current;
    const isLatest = () => requestId === ordersRequestRef.current;
    lastFetchRef.current = Date.now();

    try {
      const url = `${API_BASE_URL}/getAllorder/?shop_id=${shopId}`;
      const response = await axios.get(url);
      if (!isLatest()) return;

      let ordersArray = [];
      if (response.data && response.data.orders && Array.isArray(response.data.orders)) {
        ordersArray = response.data.orders;
      } else if (Array.isArray(response.data)) {
        ordersArray = response.data;
      }

      // Modal ke "Order" dropdown ke liye wahi list (alag request ki zaroorat nahi)
      setOrderOptions(ordersArray);

      // Shop ka naam
      let shopNameValue = '';
      if (ordersArray.length > 0 && ordersArray[0].shop_name) {
        shopNameValue = ordersArray[0].shop_name;
      } else if (ordersArray.length > 0 && ordersArray[0].shop_details?.shop_name) {
        shopNameValue = ordersArray[0].shop_details.shop_name;
      }
      if (shopNameValue) setCurrentShopName(shopNameValue);

      if (ordersArray.length === 0) {
        setOrders([]);
        setCityCounts({});
        setLoading(false);
        writeOrdersCache(shopId, { orders: [], cityCounts: {}, shopName: shopNameValue });
        if (!shopNameValue) {
          try {
            const shopResponse = await axios.get(`https://super-backend.hira-tech.co.uk/order/shops/${shopId}/`);
            if (isLatest() && shopResponse.data) setCurrentShopName(shopResponse.data.shop_name);
          } catch (err) {
            if (isLatest()) setCurrentShopName(`Shop ${shopId}`);
          }
        }
        return;
      }

      const allOrderedProducts = [];
      const CHUNK_SIZE = 8; // ek waqt me 8 orders ke products mangwao

      for (let i = 0; i < ordersArray.length; i += CHUNK_SIZE) {
        const chunk = ordersArray.slice(i, i + CHUNK_SIZE);

        const chunkResults = await Promise.all(
          chunk.map(async (order) => {
            try {
              const productsUrl = `${API_BASE_URL}/orderdProducts/${order.order_Id}?shop_id=${shopId}`;
              const productsResponse = await axios.get(productsUrl);
              return {
                order,
                data: productsResponse.data && Array.isArray(productsResponse.data) ? productsResponse.data : []
              };
            } catch (err) {
              console.error(`Error fetching products for order ${order.order_Id}:`, err);
              return { order, data: [] };
            }
          })
        );

        if (!isLatest()) return;

        chunkResults.forEach(({ order, data }) => {
          data.forEach((product) => {
            // Asli price: server ki price_per_unit (free screws ki 0 price bhi sahi dikhe)
            let unitPrice;
            if (product.price_per_unit !== undefined && product.price_per_unit !== null) {
              unitPrice = parseFloat(product.price_per_unit);
            } else if (product.custom_price !== null && product.custom_price !== undefined) {
              unitPrice = parseFloat(product.custom_price);
            } else {
              unitPrice = parseFloat(product.product_Id?.product_Price || 0);
            }

            allOrderedProducts.push({
              id: product.id,
              productId: product.product_Id?.product_Id || '',
              sku: product.product_Id?.SKU || '',
              name: product.product_Id?.product_Name || '',
              qty: product.quantity,
              price: unitPrice,
              orderId: order.order_Id,
              orderTrackingId: order.order_Tracking_Id || '',
              total_price: product.total_price !== undefined && product.total_price !== null
                ? product.total_price
                : unitPrice * (parseInt(product.quantity, 10) || 0),
              shopId: order.shop,
              shopName: order.shop_name || '',
              city: product.city || order.city || ''
            });
          });
        });

        // Pehla batch aate hi list dikhana shuru
        if (allOrderedProducts.length > 0) {
          setOrders([...allOrderedProducts]);
          setLoading(false);
        }
      }

      if (!isLatest()) return;

      const cityCountsData = {};
      allOrderedProducts.forEach(item => {
        if (item.city) {
          cityCountsData[item.city] = (cityCountsData[item.city] || 0) + 1;
        }
      });

      setOrders(allOrderedProducts);
      setCityCounts(cityCountsData);
      setLoading(false);
      writeOrdersCache(shopId, { orders: allOrderedProducts, cityCounts: cityCountsData, shopName: shopNameValue });

      if (!shopNameValue) {
        try {
          const shopResponse = await axios.get(`https://super-backend.hira-tech.co.uk/order/shops/${shopId}/`);
          if (isLatest() && shopResponse.data) setCurrentShopName(shopResponse.data.shop_name);
        } catch (err) {
          if (isLatest()) setCurrentShopName(`Shop ${shopId}`);
        }
      }
    } catch (err) {
      if (isLatest()) {
        console.error('Error fetching ordered products:', err);
        showErrorAlert('Failed to load ordered products');
        setLoading(false);
      }
    }
  }, [shopId, API_BASE_URL]);

  // ============== FETCH PRODUCTS BY SHOP ==============
  const fetchProductsByShop = useCallback(async (shop_id) => {
    if (!shop_id) {
      setProductOptions([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const url = `${PRODUCT_API_URL}/products/?shop_id=${shop_id}`;
      const response = await axios.get(url);
      if (response.data && Array.isArray(response.data)) {
        setProductOptions(response.data);
      } else {
        setProductOptions([]);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setProductOptions([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [PRODUCT_API_URL]);

  // ============== FETCH ORDERS BY SHOP ==============
  const fetchOrdersByShop = useCallback(async (shop_id) => {
    if (!shop_id) {
      setOrderOptions([]);
      return;
    }
    try {
      const url = `${API_BASE_URL}/getAllorder/?shop_id=${shop_id}`;
      const response = await axios.get(url);
      let ordersArray = [];
      if (response.data && response.data.orders && Array.isArray(response.data.orders)) {
        ordersArray = response.data.orders;
      } else if (Array.isArray(response.data)) {
        ordersArray = response.data;
      }
      setOrderOptions(ordersArray);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrderOptions([]);
    }
  }, [API_BASE_URL]);

  // ============== LOAD DATA (shop change / first visit) ==============
  useEffect(() => {
    // URL me shop nahi hai: default shop chun kar redirect
    if (!shopId) {
      fetchShops();
      return;
    }

    // Is shop ki saved list foran dikhao (agar hai)
    const cached = readOrdersCache(shopId);
    if (cached) {
      setOrders(cached.orders || []);
      setCityCounts(cached.cityCounts || {});
      if (cached.shopName) setCurrentShopName(cached.shopName);
      setLoading(false);
    } else {
      setOrders([]);
      setCityCounts({});
      setCurrentShopName('Loading...');
      setLoading(true);
    }
    setSelectedItems([]);
    setSelectedFilterCity('');

    // Sab kuch ek saath (shops ki list ka intezar nahi)
    fetchOrderedProducts();
    fetchProductsByShop(shopId);
    fetchShopCities(shopId);

    if (!hasLoadedShopsRef.current) {
      hasLoadedShopsRef.current = true;
      fetchShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, fetchOrderedProducts, fetchProductsByShop, fetchShopCities]);

  // ✅ Order kahin aur se badle / delete ho (Orders page, Order Detail) to yahan bhi foran taza data
  useEffect(() => {
    if (!shopId) return undefined;

    const refresh = () => { fetchOrderedProducts(); };
    const onFocus = () => {
      // bar bar focus par bhaari reload na ho: kam az kam 15 second ka farq
      if (Date.now() - lastFetchRef.current > 15000) fetchOrderedProducts();
    };

    window.addEventListener('orderUpdated', refresh);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('orderUpdated', refresh);
      window.removeEventListener('focus', onFocus);
    };
  }, [shopId, fetchOrderedProducts]);

  // Shops ki list aa jaye to shop ka naam usi se le lo
  useEffect(() => {
    if (!shopId || shops.length === 0) return;
    const found = shops.find(s => String(s.shop_id) === String(shopId));
    if (found?.shop_name) setCurrentShopName(found.shop_name);
  }, [shops, shopId]);

  // ============== HANDLERS ==============
  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleFilterCityChange = (e) => {
    setSelectedFilterCity(e.target.value);
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredOrders.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredOrders.map(item => item.id));
    }
    setShowActionMenu(false);
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(item => item !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
    setShowActionMenu(false);
  };

  const handleClearAll = () => {
    setSelectedItems([]);
    setShowActionMenu(false);
  };

  const handleAddProduct = () => {
    setNewOrderProduct({
      shop_id: shopId || '',
      product_Id: '',
      order_Id: '',
      quantity: 1,
      custom_price: 0,
      city: ''
    });
    if (shopId) {
      fetchProductsByShop(shopId);
      fetchOrdersByShop(shopId);
      fetchShopCities(shopId);
    }
    setShowModal(true);
  };

  const handleEditProduct = (item) => {
    setEditProduct({
      id: item.id,
      shop_id: item.shopId || shopId || '',
      product_Id: item.productId,
      order_Id: item.orderId,
      order_tracking: item.orderTrackingId || '',
      quantity: item.qty,
      custom_price: item.price,
      price_touched: false,
      city: item.city || ''
    });
    if (shopId) fetchProductsByShop(shopId);
    setEditModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewOrderProduct({
      shop_id: '',
      product_Id: '',
      order_Id: '',
      quantity: 1,
      custom_price: 0,
      city: ''
    });
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setEditProduct(emptyEditProduct);
  };

  const handleShopChangeInModal = (selectedShopId) => {
    setNewOrderProduct(prev => ({
      ...prev,
      shop_id: selectedShopId,
      product_Id: '',
      order_Id: '',
      city: ''
    }));

    if (selectedShopId) {
      fetchProductsByShop(selectedShopId);
      fetchOrdersByShop(selectedShopId);
      fetchShopCities(selectedShopId);
    } else {
      setProductOptions([]);
      setOrderOptions([]);
      setShopCities([]);
    }
  };

  // Edit me product badle to uski apni price aa jaye (agar price pata ho)
  const handleEditProductChange = (val) => {
    const p = productOptions.find(x => String(x.product_Id) === String(val));
    const hasPrice = p && p.product_Price !== undefined && p.product_Price !== null;
    setEditProduct(prev => ({
      ...prev,
      product_Id: val,
      custom_price: hasPrice ? Number(p.product_Price) : '',
      price_touched: false
    }));
  };

  const handleSaveProduct = async () => {
    if (!newOrderProduct.shop_id) {
      showWarningAlert('Please select a Shop');
      return;
    }
    if (!newOrderProduct.product_Id) {
      showWarningAlert('Please select a Product');
      return;
    }
    if (!newOrderProduct.order_Id) {
      showWarningAlert('Please select an Order');
      return;
    }

    setSaveLoading(true);

    try {
      const orderDetail = orderOptions.find(o => o.order_Id === parseInt(newOrderProduct.order_Id));

      if (!orderDetail) {
        showErrorAlert('Selected order not found');
        setSaveLoading(false);
        return;
      }

      const customerId = orderDetail.customer?.id;
      if (!customerId) {
        showErrorAlert('This order has no customer associated');
        setSaveLoading(false);
        return;
      }

      let addressId = orderDetail.address?.address_Id;
      if (!addressId) {
        showErrorAlert('Order has no address');
        setSaveLoading(false);
        return;
      }

      const payload = {
        products: [{
          product_Id: parseInt(newOrderProduct.product_Id),
          quantity: newOrderProduct.quantity,
          custom_price: newOrderProduct.custom_price || undefined
        }],
        customer: customerId,
        address: addressId,
        shop_id: parseInt(newOrderProduct.shop_id),
        city: newOrderProduct.city || ''
      };

      const response = await axios.put(`${API_BASE_URL}/update/${newOrderProduct.order_Id}/`, payload);

      if (response.status === 200) {
        showSuccessAlert('Product added successfully!');
        handleCloseModal();
        invalidateOrdersListCache();
        await fetchOrderedProducts();
        await fetchOrdersByShop(newOrderProduct.shop_id);
      }
    } catch (err) {
      console.error('Error adding product:', err);
      showErrorAlert(extractServerErrorMessage(err, 'Failed to add product'));
    } finally {
      setSaveLoading(false);
    }
  };

  // ✅ EDIT: ab usi ordered product line ko badalta hai (nayi line nahi banti),
  // aur backend order ka subtotal / VAT / total, payment aur invoice bhi dobara calculate karta hai.
  const handleUpdateProduct = async () => {
    if (!editProduct.id) {
      showWarningAlert('Nothing selected to update');
      return;
    }
    if (!editProduct.product_Id) {
      showWarningAlert('Please select a Product');
      return;
    }
    if (!(parseInt(editProduct.quantity, 10) >= 1)) {
      showWarningAlert('Quantity must be at least 1');
      return;
    }

    setSaveLoading(true);

    try {
      const payload = {
        product_Id: parseInt(editProduct.product_Id, 10),
        quantity: parseInt(editProduct.quantity, 10),
        city: editProduct.city || ''
      };

      // Price tabhi bhejo jab user ne khud badli ho (warna purani / naye product ki apni price rahe)
      if (editProduct.price_touched) {
        payload.custom_price = editProduct.custom_price === '' ? null : editProduct.custom_price;
      }

      const response = await axios.put(
        `${API_BASE_URL}/ordered-product/${editProduct.id}/update/`,
        payload
      );

      if (response.status === 200) {
        showSuccessAlert('Product updated successfully! The order has been updated too.');
        handleCloseEditModal();
        invalidateOrdersListCache();
        await fetchOrderedProducts();
      }
    } catch (err) {
      console.error('Error updating product:', err);
      // popup modal ke upar aata hai (z-index fix), aur asli wajah dikhata hai
      showErrorAlert(extractServerErrorMessage(err, 'Failed to update product'));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      setSaveLoading(true);
      try {
        await axios.delete(`${API_BASE_URL}/delete-product/${id}/`, {
          params: { shop_id: shopId }
        });
        showSuccessAlert('Product deleted successfully!');
        setSelectedItems(selectedItems.filter(item => item !== id));
        invalidateOrdersListCache();
        await fetchOrderedProducts();
      } catch (err) {
        console.error('Error deleting product:', err);
        showErrorAlert(extractServerErrorMessage(err, 'Failed to delete product'));
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      showWarningAlert('Please select products to delete');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${selectedItems.length} product(s)`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    });

    if (result.isConfirmed) {
      setSaveLoading(true);
      try {
        let deletedCount = 0;
        for (const id of selectedItems) {
          try {
            await axios.delete(`${API_BASE_URL}/delete-product/${id}/`, {
              params: { shop_id: shopId }
            });
            deletedCount++;
          } catch (err) {
            console.error(`Error deleting product ${id}:`, err);
          }
        }
        showSuccessAlert(`${deletedCount} product(s) deleted successfully!`);
        setSelectedItems([]);
        setShowActionMenu(false);
        invalidateOrdersListCache();
        await fetchOrderedProducts();
      } catch (err) {
        console.error('Error deleting products:', err);
        showErrorAlert('Failed to delete some products');
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleShopInputChange = (e) => {
    const { name, value } = e.target;
    setNewShopData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateShop = async () => {
    if (!newShopData.shop_name.trim()) {
      showWarningAlert('Please enter a shop name');
      return;
    }
    if (!newShopData.subdomain.trim()) {
      showWarningAlert('Please enter a subdomain');
      return;
    }
    await createShop(newShopData);
  };

  const handleClickOutside = useCallback((e) => {
    if (showActionMenu && !e.target.closest('.action-dropdown-container')) {
      setShowActionMenu(false);
    }
  }, [showActionMenu]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Filter by BOTH search term AND selected city
  const query = searchTerm.toLowerCase();
  const filteredOrders = orders.filter((item) => {
    if (selectedFilterCity && selectedFilterCity.trim() !== '') {
      if (item.city !== selectedFilterCity) {
        return false;
      }
    }

    return (
      String(item.name || '').toLowerCase().includes(query) ||
      String(item.sku || '').toLowerCase().includes(query) ||
      String(item.productId || '').toLowerCase().includes(query) ||
      String(item.orderId || '').includes(searchTerm) ||
      String(item.orderTrackingId || '').toLowerCase().includes(query) ||
      String(item.shopName || '').toLowerCase().includes(query) ||
      String(item.city || '').toLowerCase().includes(query)
    );
  });

  const shopDropdownOptions = shops.map(shop => ({
    value: shop.shop_id,
    label: `${shop.shop_name} (${shop.full_domain})`
  }));

  const productDropdownOptions = productOptions.map(product => ({
    value: product.product_Id,
    label: product.product_Name
  }));

  // ✅ Order ki pehchan tracking id se (jaise Orders page me)
  const orderDropdownOptions = orderOptions.map(order => ({
    value: order.order_Id,
    label: `${order.order_Tracking_Id || `#${order.order_Id}`} - ${order.order_Status || 'Pending'}`
  }));

  const cityDropdownOptions = shopCities.map(city => ({
    value: city,
    label: city
  }));

  // ============== MAIN RENDER ==============
  return (
    <div className="op-orders-container">
      <style>{SWAL_ON_TOP_CSS}</style>

      <div className="op-header-section">
        <div className="op-header-left">
          <MdOutlineProductionQuantityLimits className="op-header-icon" />
          <h2 className="op-header-title">Ordersproduct</h2>
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity
                ? `${selectedFilterCity} (${cityCounts[selectedFilterCity] || 0})`
                : `All Cities (${filteredOrders.length})`}
            </span>
          </span>
        </div>

        <div className="op-header-right">
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={loadingFilterCities || shopCities.length === 0}
            >
              <option value="">
                {loadingFilterCities ? 'Loading...' : 'All Cities'}
              </option>
              {shopCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[city] || 0})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by product name, SKU, order ID, shop, or city..."
              className="op-search-bar"
              value={searchTerm}
              onChange={handleSearch}
              disabled={saveLoading}
            />
          </div>

          <button className="op-select-btn" onClick={handleSelectAll} disabled={loading || saveLoading}>
            <FaCheckDouble />
            <span>{selectedItems.length === filteredOrders.length && filteredOrders.length > 0 ? 'Deselect All' : 'Select All'}</span>
          </button>

          <button className="op-add-btn" onClick={handleAddProduct} disabled={saveLoading}>
            <FaPlus />
            <span>ADD PRODUCT</span>
          </button>
        </div>
      </div>

      <div className="op-content">
        {selectedItems.length > 0 && (
          <div className="op-selection-bar">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedItems.length} product(s) selected
              </span>
            </div>
            <div className="action-dropdown-container">
              <button
                className="op-action-btn"
                onClick={() => setShowActionMenu(!showActionMenu)}
                disabled={saveLoading}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionMenu && (
                <div className="op-action-dropdown">
                  <button
                    className="op-delete-option"
                    onClick={handleDeleteSelected}
                    disabled={saveLoading}
                  >
                    <FaTrash />
                    <span>Delete Selected ({selectedItems.length})</span>
                  </button>
                </div>
              )}
              <button className="op-clear-btn" onClick={handleClearAll} disabled={saveLoading}>
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="op-table-container">
          <div className="op-table-wrapper">
            <table className="op-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedItems.length === filteredOrders.length && filteredOrders.length > 0}
                      onChange={handleSelectAll}
                      disabled={loading || saveLoading}
                    />
                  </th>
                  <th>Order ID</th>
                  <th>Product SKU</th>
                  <th>Product Name</th>
                  <th>Shop</th>
                  <th>City</th>
                  <th>Quantity</th>
                  <th>Price ({currency})</th>
                  <th>Total ({currency})</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Nothing is shown until the first load is done, so "No ordered products" never flashes */}
                {loading ? (
                  <tr>
                    <td colSpan="10" style={{ height: '200px' }}></td>
                  </tr>
                ) : filteredOrders.length > 0 ? (
                  filteredOrders.map((item) => (
                    <tr key={item.id} className={selectedItems.includes(item.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          disabled={saveLoading}
                        />
                      </td>
                      {/* ✅ Order ID ab tracking id jaisi (HT-00014) */}
                      <td>{item.orderTrackingId || `#${item.orderId}`}</td>
                      <td>{item.sku}</td>
                      <td>{item.name}</td>
                      <td>{item.shopName || '-'}</td>
                      <td>{item.city || '-'}</td>
                      <td>{item.qty}</td>
                      <td>{formatPrice(item.price)}</td>
                      <td>{formatPrice(item.total_price)}</td>
                      <td className="op-actions-cell">
                        <div className="op-actions-cell-inner">
                          <button
                            className="op-edit-icon-btn"
                            onClick={() => handleEditProduct(item)}
                            title="Edit"
                            disabled={saveLoading}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="op-delete-icon-btn"
                            onClick={() => handleDeleteProduct(item.id)}
                            title="Delete"
                            disabled={saveLoading}
                          >
                            <RiDeleteBinFill />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="no-data-cell">
                      <div className="no-data">
                        <FaBox className="no-data-icon" />
                        <p>
                          {selectedFilterCity
                            ? `No ordered products found in "${selectedFilterCity}".`
                            : `No ordered products found for ${currentShopName}.`}
                        </p>
                        <button
                          className="add-first-btn"
                          onClick={handleAddProduct}
                          disabled={saveLoading}
                        >
                          <FaPlus /> Add Your First Product
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="op-modal-overlay" onClick={handleCloseModal}>
          <div className="op-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Ordered Product</h3>
              <button className="op-close-btn" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-body">
              <form className="op-form" onSubmit={(e) => e.preventDefault()}>
                <div className="op-form-row">
                  <label className="op-form-label">SHOP: *</label>
                  <div className="op-form-control-wrapper">
                    <CustomDropdown
                      options={shopDropdownOptions}
                      value={newOrderProduct.shop_id}
                      onChange={handleShopChangeInModal}
                      placeholder="-- Select Shop --"
                      disabled={saveLoading || loadingShops}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">PRODUCT: *</label>
                  <div className="op-form-control-wrapper">
                    <CustomDropdown
                      options={productDropdownOptions}
                      value={newOrderProduct.product_Id}
                      onChange={(val) => setNewOrderProduct({...newOrderProduct, product_Id: val})}
                      placeholder={
                        !newOrderProduct.shop_id ? "Select a shop first" :
                        loadingProducts ? "⏳ Loading products..." :
                        productOptions.length === 0 ? "❌ No products available" : "-- Select Product --"
                      }
                      disabled={!newOrderProduct.shop_id || loadingProducts || productOptions.length === 0}
                      loading={loadingProducts}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">ORDER: *</label>
                  <div className="op-form-control-wrapper">
                    <CustomDropdown
                      options={orderDropdownOptions}
                      value={newOrderProduct.order_Id}
                      onChange={(val) => setNewOrderProduct({...newOrderProduct, order_Id: val})}
                      placeholder={
                        !newOrderProduct.shop_id ? "Select a shop first" :
                        orderOptions.length === 0 ? "No orders available" : "-- Select Order --"
                      }
                      disabled={!newOrderProduct.shop_id || orderOptions.length === 0}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">CITY:</label>
                  <div className="op-form-control-wrapper">
                    <CustomDropdown
                      options={cityDropdownOptions}
                      value={newOrderProduct.city}
                      onChange={(val) => setNewOrderProduct({...newOrderProduct, city: val})}
                      placeholder={
                        !newOrderProduct.shop_id ? "Select a shop first" :
                        shopCities.length === 0 ? "No cities available" : "-- Select City --"
                      }
                      disabled={!newOrderProduct.shop_id || shopCities.length === 0}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">QUANTITY:</label>
                  <div className="op-form-control-wrapper">
                    <input
                      type="number"
                      className="op-editable-input"
                      value={newOrderProduct.quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setNewOrderProduct({...newOrderProduct, quantity: isNaN(val) ? 1 : Math.max(1, val)});
                      }}
                      min="1"
                      disabled={saveLoading}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">CUSTOM PRICE:</label>
                  <div className="op-form-control-wrapper">
                    <input
                      type="number"
                      className="op-editable-input"
                      value={newOrderProduct.custom_price}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setNewOrderProduct({...newOrderProduct, custom_price: isNaN(val) ? 0 : Math.max(0, val)});
                      }}
                      min="0"
                      step="0.01"
                      disabled={saveLoading}
                      placeholder="Leave empty to use product price"
                    />
                  </div>
                </div>
              </form>
            </div>

            <div className="op-modal-buttons-horizontal">
              <button className="op-cancel-btn" onClick={handleCloseModal} disabled={saveLoading}>
                Cancel
              </button>
              <button className="op-save-btn" onClick={handleSaveProduct} disabled={saveLoading}>
                {saveLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div className="op-modal-overlay" onClick={handleCloseEditModal}>
          <div className="op-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Ordered Product</h3>
              <button className="op-close-btn" onClick={handleCloseEditModal}>×</button>
            </div>

            <div className="modal-body">
              <form className="op-form" onSubmit={(e) => e.preventDefault()}>
                {/* Shop aur Order badle nahi ja sakte: ye line usi order ki hai */}
                <div className="op-form-row">
                  <label className="op-form-label">SHOP:</label>
                  <div className="op-form-control-wrapper">
                    <input
                      type="text"
                      className="op-editable-input"
                      value={currentShopName}
                      readOnly
                      disabled
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">ORDER:</label>
                  <div className="op-form-control-wrapper">
                    <input
                      type="text"
                      className="op-editable-input"
                      value={editProduct.order_tracking || `#${editProduct.order_Id}`}
                      readOnly
                      disabled
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">PRODUCT:</label>
                  <div className="op-form-control-wrapper">
                    <CustomDropdown
                      options={productDropdownOptions}
                      value={editProduct.product_Id}
                      onChange={handleEditProductChange}
                      placeholder={loadingProducts ? "⏳ Loading products..." : "Select Product"}
                      disabled={loadingProducts}
                      loading={loadingProducts}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">CITY:</label>
                  <div className="op-form-control-wrapper">
                    <CustomDropdown
                      options={cityDropdownOptions}
                      value={editProduct.city}
                      onChange={(val) => setEditProduct({...editProduct, city: val})}
                      placeholder="Select City"
                      disabled={shopCities.length === 0}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">QUANTITY:</label>
                  <div className="op-form-control-wrapper">
                    <input
                      type="number"
                      className="op-editable-input"
                      value={editProduct.quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setEditProduct({...editProduct, quantity: isNaN(val) ? 1 : Math.max(1, val)});
                      }}
                      min="1"
                      disabled={saveLoading}
                    />
                  </div>
                </div>

                <div className="op-form-row">
                  <label className="op-form-label">PRICE ({currency}):</label>
                  <div className="op-form-control-wrapper">
                    <input
                      type="number"
                      className="op-editable-input"
                      value={editProduct.custom_price}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const val = parseFloat(raw);
                        setEditProduct({
                          ...editProduct,
                          custom_price: raw === '' ? '' : (isNaN(val) ? 0 : Math.max(0, val)),
                          price_touched: true
                        });
                      }}
                      min="0"
                      step="0.01"
                      disabled={saveLoading}
                      placeholder="Leave empty to use product price"
                    />
                  </div>
                </div>
              </form>
            </div>

            <div className="op-modal-buttons-horizontal">
              <button className="op-cancel-btn" onClick={handleCloseEditModal} disabled={saveLoading}>
                Cancel
              </button>
              <button className="op-save-btn" onClick={handleUpdateProduct} disabled={saveLoading}>
                {saveLoading ? 'Updating...' : 'Update Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ordersproduct;